import { useEffect, useRef, useState } from "react";
import api, { wsUrl } from "../lib/api";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Trophy, Flame, Crown, Medal, Radio } from "lucide-react";
import StreakBadges from "../components/StreakBadges";

export default function Leaderboard() {
  const [current, setCurrent] = useState([]);
  const [alltime, setAlltime] = useState([]);
  const [live, setLive] = useState(false);
  const wsRef = useRef(null);

  const load = async () => {
    try {
      const [a, b] = await Promise.all([
        api.get("/leaderboard/current"),
        api.get("/leaderboard/alltime"),
      ]);
      setCurrent(a.data);
      setAlltime(b.data);
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    load();
    const ws = new WebSocket(wsUrl("/api/ws/leaderboard"));
    wsRef.current = ws;
    ws.onopen = () => setLive(true);
    ws.onclose = () => setLive(false);
    ws.onmessage = () => load();
    return () => ws.close();
  }, []);

  return (
    <div className="grid-bg min-h-[calc(100vh-4rem)]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-display font-black text-3xl sm:text-5xl tracking-tighter text-slate-50 flex items-center gap-3">
              <Trophy className="w-10 h-10 text-amber-300" /> Leaderboard
            </h1>
            <p className="text-slate-400 mt-1">Who&apos;s ruling the arcade tonight?</p>
          </div>
          <div className={`chip ${live ? "chip-green" : "chip-slate"}`} data-testid="leaderboard-live-indicator">
            <Radio className="w-3.5 h-3.5" /> {live ? "LIVE" : "OFFLINE"}
          </div>
        </div>

        <Tabs defaultValue="current" className="w-full">
          <TabsList className="grid grid-cols-2 w-full border border-slate-700 bg-slate-900/50 p-1 rounded-xl">
            <TabsTrigger value="current" data-testid="leaderboard-tab-current-streak" className="rounded-lg data-[state=active]:bg-amber-400 data-[state=active]:text-slate-900 text-slate-300 font-semibold">
              <Flame className="w-4 h-4 mr-1" /> Current Streak
            </TabsTrigger>
            <TabsTrigger value="alltime" data-testid="leaderboard-tab-alltime-streak" className="rounded-lg data-[state=active]:bg-amber-400 data-[state=active]:text-slate-900 text-slate-300 font-semibold">
              <Crown className="w-4 h-4 mr-1" /> All-Time Best
            </TabsTrigger>
          </TabsList>

          <TabsContent value="current" className="mt-6">
            <RankList list={current} field="current_streak" testId="leaderboard-current-list" empty="Be the first with a live streak — win a match!" />
          </TabsContent>
          <TabsContent value="alltime" className="mt-6">
            <RankList list={alltime} field="best_streak" testId="leaderboard-alltime-list" empty="No records yet. Set one!" />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

function RankList({ list, field, testId, empty }) {
  if (!list.length) {
    return (
      <div className="neo-flat p-10 text-center text-slate-500" data-testid={`${testId}-empty`}>
        {empty}
      </div>
    );
  }
  const isCurrent = field === "current_streak";
  return (
    <ul className="space-y-3" data-testid={testId}>
      {list.map((u, i) => (
        <li key={u.id} className={`neo-flat p-4 flex items-center gap-4 ${i === 0 ? "glow-amber" : ""}`} data-testid={`${testId}-row-${i}`}>
          <RankBadge rank={i + 1} />
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-lg truncate text-slate-100">{u.username}</div>
            <div className="text-xs font-mono-stat text-slate-500">
              {u.wins}W / {u.losses}L / {u.ties}T
            </div>
          </div>
          <div className="flex items-center gap-3">
            {/* Primary stat for this tab, large */}
            <div className={`font-mono-stat text-3xl font-black flex items-center gap-1 ${isCurrent ? "text-neon-amber" : "text-neon-amber"}`} data-testid={`${testId}-row-${i}-primary`}>
              {isCurrent ? <Flame className="w-6 h-6" /> : <Trophy className="w-6 h-6" />}
              {u[field]}
            </div>
            {/* Companion stat, small */}
            <div className="hidden sm:block">
              <StreakBadges current={u.current_streak || 0} best={u.best_streak || 0} />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

function RankBadge({ rank }) {
  if (rank === 1) return <div className="w-10 h-10 rounded-full bg-amber-400/10 border border-amber-400/60 flex items-center justify-center"><Crown className="w-5 h-5 text-amber-300" /></div>;
  if (rank === 2) return <div className="w-10 h-10 rounded-full bg-slate-500/10 border border-slate-400/60 flex items-center justify-center"><Medal className="w-5 h-5 text-slate-300" /></div>;
  if (rank === 3) return <div className="w-10 h-10 rounded-full bg-orange-500/10 border border-orange-400/60 flex items-center justify-center"><Medal className="w-5 h-5 text-orange-300" /></div>;
  return <div className="w-10 h-10 rounded-full bg-slate-800 border border-slate-700 flex items-center justify-center font-mono-stat font-bold text-slate-300">{rank}</div>;
}
